// Measurement writes (docs/PEDAGOGY.md §5.3, phase 1). Everything is measured from what the
// learners already do; nothing here waits for the network or can break a session:
// - exercise items and their words go to the review outbox as exercise reviews (like listening
//   results, listen/review.ts), sent with the next sync;
// - activity time, the session tap and recording scores go through the ordered write outbox
//   (queueWrite → POST /api/measure/…, idempotent by client id), so they work offline too.
// Every function swallows its own errors (a measurement is never worth a crash) and writes
// nothing in the dev demo.
import type { ReviewRow } from '@valyrio/engine/api';
import {
  recordingSummary,
  touchedLemmas,
  type ActivityTimeEntry,
  type AnalysisLike,
  type MeasureLog,
  type RecordingScoreEntry,
  type SessionRatingEntry,
} from '@valyrio/engine/pedagogy/logs';
import { strategyOf, type ActivityId } from '@valyrio/engine/pedagogy/strategies';
// per-person stress feedback: a reference take's word results (engine pedagogy/stress-focus.ts)
import { stressWordResults, type FocusWordInfo } from '@valyrio/engine/pedagogy/stress-focus';
import { todayLocal } from '../lib/time';
import { getDB } from '../lib/db';
import { uid } from '../lib/sync';
import { queueWrite } from '../state/actions';
import { store } from '../state/store';
// Yksi puhelin: a measurement made in the partner's turn on this phone is theirs (together/onephone/)
import { activityTwin, actingPartner, type Actor } from '../together/onephone/acting';

/** Outbox kinds (Asetukset → Hylätyt muutokset lists a refused one by kind). */
export const MEASURE_KINDS = { time: 'measure-time', rating: 'measure-rating', score: 'measure-score' } as const;

const demo = () => store.get().identity === 'demo';

function quietly(what: string, p: Promise<unknown>): void {
  p.catch((err: unknown) => console.warn(`[measure] ${what} not queued`, err));
}

// ---------- exercise items ----------

/** What the runner knows of one answered item. */
export interface ExerciseMeasure {
  /** The item's id (ex.id or the stress item's id). */
  id: string;
  /** Exercise kind ('fill', 'case', 'order', 'translate', 'stress' …). */
  kind: string;
  correct: boolean;
  close: boolean;
  activity: ActivityId;
  /** Shown → checked (ms). */
  ms?: number | null;
  lemmas: readonly (string | undefined | null)[];
}

/** One exercise item as a review_log row (mode 'exercise': no card, no schedule moves). */
export function exerciseReviewRow(m: ExerciseMeasure, now = new Date()): ReviewRow {
  const log: MeasureLog & { kind: 'exercise'; exercise: string } = {
    kind: 'exercise',
    exercise: m.kind,
    strategy: strategyOf(m.activity),
    activity: m.activity,
    verdict: m.correct ? 'correct' : m.close ? 'close' : 'wrong',
    lemmas: touchedLemmas(m.lemmas),
  };
  if (typeof m.ms === 'number' && Number.isFinite(m.ms) && m.ms >= 0 && m.ms <= 10 * 60_000) log.ms = Math.round(m.ms);
  return {
    id: uid(),
    itemId: `ex:${m.id}`.slice(0, 200),
    type: 'hv-native',
    grade: m.correct ? 3 : m.close ? 2 : 1,
    mode: 'exercise',
    reviewedAt: now.toISOString(),
    log,
    isNew: false,
  };
}

export function recordExerciseResult(m: ExerciseMeasure, actor: Actor | null = actingPartner()): void {
  if (demo()) return;
  // the partner's turn on one phone: their review log, with their pass (never this phone's /api/sync)
  if (actor) return void partnerReview(exerciseReviewRow(m), actor);
  quietly(
    'exercise result',
    getDB().then((db) => db.put('reviewOutbox', exerciseReviewRow(m))),
  );
}

/** Outbox kind of the partner's exercise rows on one phone (POST /api/acting/reviews). */
export const PARTNER_REVIEW_KIND = 'partner-review';

/** One exercise row of the partner's, made on this phone (Yksi puhelin). */
export function partnerReview(row: ReviewRow, actor: Actor): void {
  quietly('partner exercise result', queueWrite('POST', '/api/acting/reviews', { reviews: [row] }, PARTNER_REVIEW_KIND, { as: actor }));
}

// ---------- activity time ----------

export function recordActivityTime(rows: readonly Omit<ActivityTimeEntry, 'id' | 'strategy'>[]): void {
  if (demo()) return;
  for (const r of rows) {
    const body: ActivityTimeEntry = { id: uid(), strategy: strategyOf(r.activity), ...r };
    quietly('activity time', queueWrite('POST', '/api/measure/activity-time', body, MEASURE_KINDS.time));
    // Yksi puhelin: the couple session's time on one phone is the partner's too
    const twin = activityTwin(r.activity);
    if (twin) quietly('activity time', queueWrite('POST', '/api/measure/activity-time', { ...body, id: uid() }, MEASURE_KINDS.time, { as: twin }));
  }
}

// ---------- the tap ----------

export function recordSessionRating(e: SessionRatingEntry, as: Actor | null = null): void {
  if (demo()) return;
  // `as`: the partner's own tap on one phone (Yksi puhelin)
  quietly('session rating', queueWrite('POST', '/api/measure/session-rating', e, MEASURE_KINDS.rating, { as }));
}

// ---------- recording scores ----------

/** Takes already scored (an analysis shown again for the same take writes no second row). */
const scored = new WeakSet<Blob>();

/** The summary of one analysed take; nothing when nothing could be judged. Returns the row queued. */
export function recordRecordingScore(a: {
  blob: Blob;
  target: string;
  hv: string;
  activity: ActivityId;
  results: AnalysisLike;
  now?: Date;
  /** Reads a word for its stress result (the lexicon's analyser): its dictionary word, a name. */
  info?: (form: string, text: string, index: number) => FocusWordInfo;
  /** The pace timezone (the take's local day). */
  tz?: string;
}): RecordingScoreEntry | null {
  if (scored.has(a.blob)) return null;
  // the partner's take on one phone: their score (read now, before anything is awaited)
  const actor = actingPartner();
  scored.add(a.blob);
  const sum = recordingSummary(a.results);
  if (!sum) return null;
  const lemma = a.target.startsWith('lemma:') ? a.target.slice('lemma:'.length) : null;
  const e: RecordingScoreEntry = {
    id: uid(),
    target: a.target.slice(0, 200),
    hv: a.hv.slice(0, 1000),
    activity: a.activity,
    strategy: strategyOf(a.activity),
    ...sum,
    lemmas: touchedLemmas([lemma]),
    at: (a.now ?? new Date()).toISOString(),
  };
  // the words, only when measured against Peterson's clip of the same text (stress-focus.ts)
  const words = stressWordResults(a.results, a.info);
  if (words.length) {
    e.day = todayLocal(a.now ?? new Date(), a.tz);
    e.words = words;
  }
  if (!demo()) quietly('recording score', queueWrite('POST', '/api/measure/recording-score', e, MEASURE_KINDS.score, { as: actor }));
  return e;
}
